import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isEqual as _isEqual, cloneDeep as _cloneDeep } from 'lodash';

import { Grid, Paper, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';

import Validator, {
    useValidate,
    checkValidity,
} from '../../../../components/validator';

import DataContext from '../../../../data/data-context';
import Auth from '../../../../data/auth-service';

const useStyles = makeStyles((theme) => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    fullWidth: {
        width: '100%',
    },
    fullWidthInput: {
        marginTop: '12px',
        width: '100%',
        '& diaws.as_conf.auto_scale_groups.MuiInputBase-root': {
            width: '100%',
        },
    },
    labelScale: {
        fontSize: '0.750rem',
    },
}));

const CreateScaleGroups = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [changes, setChanges] = useState(false);
    const [awsGroup, setAwsGroup] = useState({
        name: '',
        group_scaling_policy: {
            metric_type: 'CPU Usage (Pct)',
            target_val_avg: 80,
            target_val_min: 1,
            target_val_max: 100,
        },
        target_val: 80,
        instances_max: 1,
        instances_min: 1,
        gre_key: '',
        gre_remote: '',
        sensor_pass: '',
    });

    const [aws, setAWS] = useState({
        as_conf: {
            auto_scale_groups: [],
            enabled: true,
            groups_mon_freq: 2,
            region: '',
            scaling_policies: [],
            stats_mon_freq: 15,
            user: '',
        },
        user_creds: {
            access_key_id: '',
            secret_access_key: '',
        },
    });

    const awsForm = useValidate({});
    const awsPerms = Auth.permissions.get('AWS');
    const [readonly] = useState(!awsPerms.write);
    const routeParams = useParams();
    const [isUpdate, setIsUpdate] = useState(false);
    const [updateGroup, setUpdateGroup] = useState(false);
    const [valid, setValid] = useState(false);

    useEffect(() => {
        setValid(
            (awsGroup.name || '').length > 0 &&
            (awsGroup.sensor_pass || '').length > 0 &&
            checkValidity(awsForm.get())
        );

        setChanges(!_isEqual(awsGroup, updateGroup));
    }, [awsGroup, updateGroup, awsForm]);

    useEffect(() => {
        if (systemSettings && systemSettings.aws) {
            const a = _cloneDeep(systemSettings.aws);
            if (routeParams.id) {
                setIsUpdate(true);
                const groupForm = a.as_conf.auto_scale_groups.filter(
                    (group, idx) => idx === parseInt(routeParams.id)
                )[0];
                setAwsGroup(groupForm);
                setUpdateGroup(_cloneDeep(groupForm));
            }
            const isChange = !_isEqual(aws, systemSettings.aws);
            if (isChange) {
                if (!a.as_conf.auto_scale_groups) {
                    a.as_conf.auto_scale_groups = [];
                }
                setAWS(a);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [systemSettings]);

    const handleSave = () => {
        let customToast = '';
        if (isUpdate) {
            aws.as_conf.auto_scale_groups[parseInt(routeParams.id)] = awsGroup;
            customToast = 'Updated Auto Scale Group';
        } else {
            aws.as_conf.auto_scale_groups.push(awsGroup);
            customToast = 'Created Auto Scale Group';
        }
        setTimeout(() => {
            updateSystemSettings(_cloneDeep({ aws }), customToast);
        });
        navigate('/system/port_mirroring/aws');
    };

    const handleCancel = () => {
        navigate('/system/port_mirroring/aws');
    };

    const handleUpdateAutoScaleGroup = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });

        const a = { ...awsGroup };
        a[e.target.name] = e.target.type === 'number' ? parseInt(e.target.value) : e.target.value;

        setAwsGroup(a);
    };

    const handleUpdateAutoScaleGroupSelect = (e) => {
        const a = { ...aws };
        const b = { ...awsGroup };
        const idx = a.as_conf.scaling_policies.findIndex(
            (v) => v.metric_type === e.target.value
        );

        b.group_scaling_policy = a.as_conf.scaling_policies[idx];
        b.target_val = a.as_conf.scaling_policies[idx].target_val_avg;
        setAwsGroup(b);
    };

    const handleValidationRemoval = (id) => {
        const a = awsForm.get();
        delete a[id];
        awsForm.set(a);
        setAwsGroup(awsGroup);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper>
                    <Box p={5}>
                        <Grid container>
                            <Grid item xs={12} container>
                                <Box>
                                    <Typography variant="h6">
                                        {readonly ? 'View Auto Scaling Group'
                                            : (isUpdate
                                                ? 'Update Auto Scaling Group'
                                                : 'Create Auto Scaling Group')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} container spacing={2} marginTop={2}>
                                <Grid item xs={3}>
                                    <Validator
                                        errorText="Group name cannot be empty"
                                        fullWidth
                                        label="Group Name"
                                        name="name"
                                        value={awsGroup.name}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormControl className={classes.fullWidth}>
                                        <InputLabel variant="outlined" id="scale-policy-label">
                                            Scaling Policy
                                        </InputLabel>
                                        <Select
                                            label="Scale Policy"
                                            className={classes.fullWidth}
                                            value={awsGroup.group_scaling_policy.metric_type}
                                            onChange={(e) => {
                                                handleUpdateAutoScaleGroupSelect(e);
                                            }}
                                            disabled={readonly}
                                        >
                                            {aws.as_conf.scaling_policies.map((s, i) => {
                                                return (
                                                    <MenuItem
                                                        key={`group-scale-policy-${awsGroup.name}-${i}`}
                                                        value={s.metric_type}
                                                    >
                                                        {s.metric_type}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                    <Validator
                                        valueType="range"
                                        errorText={`Target Value must be within ${awsGroup.group_scaling_policy.target_val_min} and ${awsGroup.group_scaling_policy.target_val_max}`}
                                        fullWidth
                                        label="Target Value"
                                        type="number"
                                        inputProps={{
                                            min: awsGroup.group_scaling_policy.target_val_min,
                                            max: awsGroup.group_scaling_policy.target_val_max,
                                        }}
                                        name="target_val"
                                        value={awsGroup.target_val}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Validator
                                        valueType="range"
                                        errorText="Instances max must be within 0 and 100"
                                        fullWidth
                                        label="Instances Max"
                                        type="number"
                                        inputProps={{
                                            min: 0,
                                            max: 100,
                                        }}
                                        name="instances_max"
                                        value={awsGroup.instances_max}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Validator
                                        valueType="range"
                                        errorText={`Instances min must be within 0 and the max instances (${awsGroup.instances_max})`}
                                        fullWidth
                                        label="Instances Min"
                                        type="number"
                                        inputProps={{
                                            min: 0,
                                            max: awsGroup.instances_max,
                                        }}
                                        name="instances_min"
                                        value={awsGroup.instances_min}
                                        kick={awsGroup.instances_max}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Validator
                                        fullWidth
                                        label="GRE Key"
                                        name="gre_key"
                                        value={awsGroup.gre_key}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Validator
                                        valueType="ip"
                                        errorText="GRE Remote IP cannot be empty"
                                        fullWidth
                                        label="GRE Remote IP"
                                        name="gre_remote"
                                        value={awsGroup.gre_remote}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Validator
                                        errorText="Sensor password cannot be empty"
                                        fullWidth
                                        label="Sensor Password"
                                        name="sensor_pass"
                                        type="password"
                                        value={awsGroup.sensor_pass}
                                        onChange={(e, validID, valid) => {
                                            handleUpdateAutoScaleGroup(e, validID, valid);
                                        }}
                                        disabled={readonly}
                                        onRemoval={handleValidationRemoval}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} mt={2} container justifyContent="flex-end">
                            <Box mr={2}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    aria-label="Cancel"
                                    onClick={handleCancel}
                                >{readonly ? 'Back' : 'Cancel'}</Button>
                            </Box>
                            {!readonly && <Button
                                variant="contained"
                                color="primary"
                                aria-label="Save"
                                onClick={handleSave}
                                disabled={!valid || !changes}
                            >
                                {isUpdate ? 'Update' : 'Save'}
                            </Button>}
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CreateScaleGroups;
